import {Flex} from 'exsportia-components'
import {FC, useMemo} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import {useStoreGoods} from '../../api/store'
import {TabButton, TabButtonProps} from '../../ui/components/tab-button/tab-button'
import {LocaleOptions, useLocale} from '../../utils/i18n'

const defaultLOptions: LocaleOptions = {
  caseAction: 'capitalize',
}

export type NavMenuProps = {
  tabsSettings?: TabButtonProps[]
  notForMobile?: boolean
  isNested?: boolean
  /** Only tabs an anonymous visitor can use (home + store). The store catalogue
   *  is public by design, so a signed-out shopper still needs a way in. */
  publicOnly?: boolean
}

const tabsToDisplay = ['home', 'store', 'bookings', 'subscription-bookings', 'orders']

export const NavMenu: FC<NavMenuProps> = ({
  tabsSettings,
  notForMobile = false,
  isNested = false,
  publicOnly = false
}) => {
  const {l} = useLocale()
  const {pathname} = useLocation()
  const navigate = useNavigate()
  const {venueId} = useParams()
  // The Store tab appears only for venues that actually sell something.
  const {data: storeGoods} = useStoreGoods(venueId)
  const hasStore = (storeGoods?.items.length || 0) > 0
  const menuSettings: TabButtonProps[] = useMemo(() => {
    if (tabsSettings) {
      return tabsSettings
    }
    const pathArr = pathname.split('/').filter((el) => el !== '')
    const activeTab = tabsToDisplay.includes(pathArr[1]) ? pathArr[1] : 'home'
    const publicTabs = [
      {
        text: l('home', defaultLOptions),
        isActive: activeTab === 'home',
        onClick: () => navigate(`/${venueId}`),
      },
      ...(hasStore ? [{
        text: l('store-title', defaultLOptions),
        isActive: activeTab === 'store' || activeTab === 'orders',
        onClick: () => navigate(`/${venueId}/store`),
      }] : []),
    ]
    if (publicOnly) {
      // Home alone is not a menu — only render once there is a store to reach.
      return hasStore ? publicTabs : []
    }
    return [
      ...publicTabs,
      {
        text: l('bookings', defaultLOptions),
        isActive: activeTab === 'bookings',
        onClick: () => navigate(`/${venueId}/bookings`),
      },
      {
        text: l('subscriptions', defaultLOptions),
        isActive: activeTab === 'subscription-bookings',
        onClick: () => navigate(`/${venueId}/subscription-bookings`, {replace: true}),
      }
    ]
  }, [l, pathname, venueId, tabsSettings, hasStore, publicOnly])
  if (menuSettings.length === 0) {
    return null
  }
  return (
    <Flex
      justifyContent={'flex-start'}
      width={isNested ? '100%' : 'max-content'}
      display={notForMobile ? ['none', 'flex'] : 'flex'}
      height='64px'
      borderBottom={isNested ? 'solid 1px #BFC1C755' : undefined}
    >
      {menuSettings.map((settings, index) => (
        <TabButton
          key={`nav-tab-${index}`}
          icon={settings.icon}
          text={settings.text}
          onClick={settings.onClick}
          isActive={settings.isActive}
        />
      ))}
    </Flex>
  )
}
